import { createApi } from "@reduxjs/toolkit/query/react";
import { Integration, KeyRoles, RequestMethods } from "types/enums";
import {
  AccountFilters,
  AccountList,
  AnalyseList,
  CampaignId,
  Engagement,
  ListQuery,
  Penetration,
} from "../types";
import {
  AnalyseResponse,
  Analytics,
  CRMListItem,
  ExportView,
  IntegrationType,
  Response,
  TitleGaps,
} from "../types/apiTypes";
import { prepareQuery } from "../utils/prepareQuery";
import { baseQueryWithReauth } from "./baseQuery";

export const accountsApi = createApi({
  reducerPath: "accountsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "export-view",
    "analytics",
    "list-view",
    "filters",
    "account-titles",
  ] as const,
  endpoints: (builder) => ({
    getAccountsList: builder.query<AccountList, ListQuery>({
      query: ({ campaign_id, ...rest }) => ({
        url: `/campaigns/${campaign_id}/list-view${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      providesTags: ["list-view"],
      keepUnusedDataFor: 0,
    }),
    getAccountsFilters: builder.query<AccountFilters, ListQuery>({
      query: ({ campaign_id, ...rest }) => ({
        url: `/campaigns/${campaign_id}/filters`,
        method: RequestMethods.GET,
        params: rest,
      }),
      providesTags: ["filters"],
    }),
    getPenetrationView: builder.query<Penetration[], ListQuery>({
      query: ({ campaign_id, ...rest }) => ({
        url: `/campaigns/${campaign_id}/penetration-view${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
    }),
    getEngagementView: builder.query<Engagement[], ListQuery>({
      query: ({ campaign_id, ...rest }) => ({
        url: `/campaigns/${campaign_id}/engagement-view${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
    }),
    analyseAll: builder.mutation<AnalyseResponse, CampaignId>({
      query: ({ campaign_id }) => ({
        url: `/accounts/${campaign_id}/analyse-all`,
        method: RequestMethods.POST,
        body: { campaign_id },
      }),
    }),
    analyse: builder.mutation<AnalyseResponse, AnalyseList & CampaignId>({
      query: ({ campaign_id, ...body }) => ({
        url: `/accounts/${campaign_id}/analyse`,
        method: RequestMethods.POST,
        body,
      }),
    }),
    discardAccount: builder.mutation<Response, ListQuery>({
      query: ({ account_id, campaign_id }) => ({
        url: `/accounts/${campaign_id}/${account_id}`,
        method: RequestMethods.DELETE,
      }),
      invalidatesTags: ["list-view", "filters"],
    }),
    discardCampaign: builder.mutation<Response, string>({
      query: (campaign_id: string) => ({
        url: `/campaigns/${campaign_id}`,
        method: RequestMethods.DELETE,
      }),
    }),
    exportList: builder.query<ExportView, ListQuery>({
      query: ({ campaign_id, ...rest }) => ({
        url: `/campaigns/${campaign_id}/export-view${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      providesTags: ["export-view"],
    }),
    getAnalytics: builder.query<Analytics, ListQuery>({
      query: ({ campaign_id, ...rest }) => ({
        url: `/campaigns/${campaign_id}/analytics${prepareQuery(rest)}`,
        method: RequestMethods.GET,
      }),
      providesTags: ["analytics"],
    }),

    getCRMList: builder.query<CRMListItem[], IntegrationType>({
      query: (crm: string) => ({
        url: `/lists/${crm}`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 0,
    }),

    getCRMListByCampaign: builder.query<
      CRMListItem[],
      { campaign_id: string; crm: IntegrationType }
    >({
      query: ({ campaign_id, crm }) => ({
        url: `/lists/${campaign_id}/${crm}`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 0,
    }),
    getAccountTitleGaps: builder.query<
      TitleGaps,
      { account_id: string; role?: KeyRoles }
    >({
      query: ({ account_id, role }) => ({
        url: `/accounts/${account_id}/title-gaps${role ? `?role=${role}` : ""}`,
        method: RequestMethods.GET,
      }),
      providesTags: ["account-titles"],
    }),
    findExternalDataForAccount: builder.mutation<
      Response,
      TitleGaps & { account_id: string; campaign_id: string }
    >({
      query: ({ account_id, campaign_id, ...body }) => ({
        url: `/accounts/${campaign_id}/${account_id}/find-external-data`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    getSuggestTitles: builder.mutation<
      { suggestions: string[] },
      {
        account_id: string;
        role?: KeyRoles;
        existing_titles: string[];
      }
    >({
      query: ({ account_id, ...body }) => ({
        url: `/accounts/${account_id}/title-suggestions`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    sendIntegrationAuth: builder.mutation<
      Response,
      { client_email: string; platform: Integration } & CampaignId
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/integrations/auth/${campaign_id}`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    resendIntegrationAuth: builder.mutation<
      Response,
      { client_email: string; platform: Integration } & CampaignId
    >({
      query: ({ campaign_id, ...body }) => ({
        url: `/integrations/auth/${campaign_id}/resend`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    cancelIntegrationAuth: builder.mutation<
      Response,
      { platform: Integration } & CampaignId
    >({
      query: ({ campaign_id, platform }) => ({
        url: `/integrations/auth/${campaign_id}`,
        method: RequestMethods.DELETE,
        params: { platform },
      }),
    }),

    verifyIntegrationToken: builder.query<
      Response,
      { token: string; platform: Integration }
    >({
      query: ({ token, platform }) => ({
        url: `/integrations/auth/verify?token=${token}&platform=${platform}`,
        method: RequestMethods.GET,
      }),
    }),
    completeHubspotIntegration: builder.mutation<
      Response,
      { token: string; code: string }
    >({
      query: (body) => ({
        url: `/integrations/hubspot/complete`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    completeSalesforceIntegration: builder.mutation<
      Response,
      { token: string; code: string }
    >({
      query: (body) => ({
        url: `/integrations/salesforce/complete`,
        method: RequestMethods.POST,
        body,
      }),
    }),

    getMarketoList: builder.query<CRMListItem[], string>({
      query: (campaign_id) => ({
        url: `/lists/${campaign_id}/marketo`,
        method: RequestMethods.GET,
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetAccountsListQuery,
  useGetAccountsFiltersQuery,
  useGetPenetrationViewQuery,
  useGetEngagementViewQuery,
  useAnalyseAllMutation,
  useAnalyseMutation,
  useDiscardAccountMutation,
  useExportListQuery,
  useGetAnalyticsQuery,
  useLazyGetCRMListQuery,
  useGetAccountTitleGapsQuery,
  useLazyGetAccountTitleGapsQuery,
  useFindExternalDataForAccountMutation,
  useGetSuggestTitlesMutation,
  useLazyGetCRMListByCampaignQuery,
  useCancelIntegrationAuthMutation,
  useLazyGetMarketoListQuery,
  useSendIntegrationAuthMutation,
  useResendIntegrationAuthMutation,
  useCompleteHubspotIntegrationMutation,
  useCompleteSalesforceIntegrationMutation,
  useLazyVerifyIntegrationTokenQuery,
} = accountsApi;
